import { computed, inject, Injectable, signal, Signal } from "@angular/core";
import { TemplateRef } from "@angular/core";
import { OmniarkDrawerService } from "../../../../../../shared/services/drawer.service";
import { PublishIssue } from "../../../services/enrichment-data.service";
import { FeatureFlagService } from "@core/services/feature-flag.service";
import {
  PublishSchedule,
  PublishScheduleAction,
  openScheduleFor,
} from "@app/shared/models/publish-schedule.model";
import {
  BANGKOK_ZONE_LABEL,
  formatScheduledAt,
  isFutureSchedule,
  toScheduledAt,
} from "@app/shared/utils/publish-schedule.util";
import { PriceEntry } from "../../../utils/price-entry.util";
import {
  LifecycleGate,
  lifecycleActions,
} from "../../../utils/price-lifecycle.util";
import { FieldTarget } from "../field-focus";
import { ActivePriceReadiness } from "./active-price-readiness";
import { ActivePriceStore } from "./active-price.store";

export type DrawerMode = "publish" | "reject";

/**
 * Whether the confirmed action happens on confirm or is queued for a chosen
 * time. Orthogonal to {@link DrawerMode}: the two combine into the four calls
 * the drawer can make — publish now, schedule a publish, reject now, schedule
 * a take-down.
 */
export type DrawerTiming = "now" | "later";

/** The schedule direction a {@link DrawerMode} arms, for reading a price's
 *  open rows. Reject takes a price off sale, so it queues an `unpublish`. */
export const timingActionFor = (mode: DrawerMode): PublishScheduleAction =>
  mode === "reject" ? "unpublish" : "publish";

export interface DrawerTarget {
  readonly entry: PriceEntry;
  readonly eligibility: LifecycleGate;
  /** This price's open schedule in the drawer's direction, if it has one.
   *  Only blocks the `later` timing — a price queued for 1 Oct can still be
   *  published by hand today, and the backend leaves the queued row armed. */
  readonly openSchedule: PublishSchedule | null;
}

/** Exactly what Proceed sends. `priceIds` is a one-tuple: the type cannot
 *  say two prices, and an empty list would mean "every designed price" on
 *  the wire. */
export interface DrawerRequest {
  readonly action: DrawerMode;
  readonly priceIds: readonly [string];
  /** RFC 3339, or "" when acting now. */
  readonly scheduledAt: string;
}

/**
 * Owns the publish drawer's state and the navigation that fires when the user
 * clicks an issue.
 */
@Injectable()
export class PublishDrawerController {
  private readonly drawer = inject(OmniarkDrawerService);
  private readonly featureFlags = inject(FeatureFlagService);
  private readonly active = inject(ActivePriceStore);
  private readonly readiness = inject(ActivePriceReadiness);

  /** The action the open drawer is reviewing. */
  readonly mode = signal<DrawerMode>("publish");

  /**
   * `enablePublishSchedule`. Off, the drawer is exactly what it was before
   * scheduling existed: no timing choice, no picker, and not one call to a
   * schedule route — which the backend does not mount with its own flag off.
   */
  readonly scheduleEnabled = computed(() =>
    this.featureFlags.get("enablePublishSchedule"),
  );

  /** Now, or queued for {@link scheduledAtLocal}. Reset to `now` on every
   *  open so a drawer never reopens already armed. */
  readonly timing = signal<DrawerTiming>("now");

  /** The picker's local value, "YYYY-MM-DDTHH:mm" with no zone — read as
   *  Bangkok time by `toScheduledAt` at send time. */
  readonly scheduledAtLocal = signal("");
  /** Read at each pick and when Proceed asks for the request. A time can
   *  pass while the drawer is open, and a computed that read the clock
   *  itself would keep its first answer. */
  private readonly now = signal(Date.now());

  /** Every schedule of this offering on this channel, seeded by the shell
   *  when the drawer opens. */
  readonly schedules = signal<readonly PublishSchedule[]>([]);

  readonly scheduleZoneLabel = BANGKOK_ZONE_LABEL;

  /** The schedule direction this drawer arms. */
  readonly scheduleAction = computed<PublishScheduleAction>(() =>
    timingActionFor(this.mode()),
  );

  /** True once a time is picked but it is not in the future — the API
   *  refuses a past `scheduledAt` with a 400, so the drawer says so first.
   *  Silent while the picker is still empty: an untouched field is not an
   *  error the operator needs shouted at them. */
  readonly scheduledAtIsPast = computed(
    () =>
      !!this.scheduledAtLocal() &&
      !isFutureSchedule(this.scheduledAtLocal(), new Date(this.now())),
  );

  /** The RFC 3339 timestamp the request will carry, or "" when the drawer is
   *  acting now or nothing valid is picked. */
  readonly scheduledAt = computed(() =>
    this.timing() === "later" ? toScheduledAt(this.scheduledAtLocal()) : "",
  );

  readonly target: Signal<DrawerTarget | null> = computed(() => {
    const entry = this.active.entry();
    const price = this.active.price();
    if (!entry || !price) return null;
    const actions = lifecycleActions(price);
    return {
      entry,
      eligibility: this.mode() === "reject" ? actions.reject : actions.publish,
      openSchedule: openScheduleFor(
        this.schedules(),
        entry.id,
        this.scheduleAction(),
      ),
    };
  });

  /** Why `later` is refused for this price: it is already queued in this
   *  direction, and the API answers a second row with 409. */
  readonly openScheduleHint = computed<string | null>(() => {
    const open = this.target()?.openSchedule;
    if (!open || this.timing() !== "later") return null;
    const when = formatScheduledAt(open.scheduledAt);
    return when ? `Already scheduled ${when}` : "Already scheduled";
  });

  readonly canProceed = computed<boolean>(() => {
    const target = this.target();
    if (!target?.eligibility.ok) return false;
    if (this.timing() === "later") {
      if (!isFutureSchedule(this.scheduledAtLocal(), new Date(this.now()))) {
        return false;
      }
      if (target.openSchedule) return false;
    }
    return this.mode() === "reject" || this.readiness.publish().canPublish;
  });

  /** What Proceed sends, or null when nothing may be sent. Reads the clock
   *  first: a time that passed while the drawer was open is not sent, and
   *  the drawer then says so. */
  request(): DrawerRequest | null {
    this.now.set(Date.now());
    const target = this.target();
    if (!target || !this.canProceed()) return null;
    return {
      action: this.mode(),
      priceIds: [target.entry.id],
      scheduledAt: this.scheduledAt(),
    };
  }

  private focusField: ((target: FieldTarget) => void) | null = null;

  bindFocusField(focusField: (target: FieldTarget) => void): void {
    this.focusField = focusField;
  }

  setTiming(timing: DrawerTiming): void {
    this.timing.set(timing);
  }

  setScheduledAtLocal(value: string): void {
    this.now.set(Date.now());
    this.scheduledAtLocal.set(value);
  }

  setSchedules(schedules: readonly PublishSchedule[]): void {
    this.schedules.set(schedules);
  }

  /** A drawer never reopens already armed: the timing choice and the picked
   *  time are the operator's answer to THIS opening, not sticky state. */
  openDrawer(
    template: TemplateRef<unknown>,
    mode: DrawerMode = "publish",
  ): void {
    this.mode.set(mode);
    this.timing.set("now");
    this.scheduledAtLocal.set("");
    this.drawer.showDrawerRef(template, {
      title: mode === "reject" ? "Review & reject" : "Review & publish",
      description: this.active.entry()?.title ?? "",
      width: "440px",
      position: "right",
      showCloseButton: true,
      backdropDismiss: true,
    });
  }

  closeDrawer(): void {
    this.drawer.closeAll();
  }

  navigateToIssue(issue: PublishIssue, fallbackTabId: string): void {
    this.focusField?.({
      tabId: issue.tabId ?? fallbackTabId,
      sectionId: issue.sectionId,
      fieldKey: issue.fieldKey,
    });
    this.drawer.closeAll();
  }

  /** Severity → "Fix" / "Review" prefix used in the drawer template. */
  publishIssuePrefix(issue: PublishIssue): string {
    return issue.severity === "error" ? "Fix" : "Review";
  }
}
